/**
 * Service detail row actions
 * Provides confirm/reject/cancel actions for service detail tables
 */
import type { DataTableAction } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import CancelIcons from '@/icons/CancelIcons'
import ConfirmServiceIcons from '@/icons/ConfirmServiceIcons'
import { confirmService, rejectedService } from '@/modules/Service/services'
import { ServiceStatus } from '@/modules/Service/types'
import { TFunction } from 'i18next'
import type { ServiceDetailRow } from './types'

/**
 * Minimal row shape the actions read. Lets the hook be reused by tables whose
 * row type differs from ServiceDetailRow (e.g. the CDN per-configoption rows),
 * without casting — any row exposing these fields works.
 */
type ServiceActionRow = {
    service_id?: number
    service_status?: string
    _rowType?: 'main' | 'addon' | 'total'
}

/**
 * Hook to get service detail row actions
 * Actions: Confirm, Reject, Cancel
 */
export const useServiceDetailActions = <
    T extends ServiceActionRow = ServiceDetailRow,
>(
    t: TFunction
): DataTableAction<T>[] => {
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    const { mutateAsync: confirmMutate } = useMutationWithHandler(
        {
            mutationFn: confirmService,
        },
        {
            successMessage: t('confirm-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICE,
        }
    )

    const { mutateAsync: rejectMutate } = useMutationWithHandler(
        {
            mutationFn: rejectedService,
        },
        {
            successMessage: t('rejected-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
        }
    )

    // Check if row is total (no actions for total row)
    const isTotalRow = (row: T) => {
        return row._rowType === 'total'
    }

    return [
        {
            key: 'confirm',
            label: t('confirm-service'),
            icon: <ConfirmServiceIcons />,
            variant: 'success',
            onClick: (row) => {
                if (!row.service_id) return
                setConfirmation({
                    open: true,
                    title: t('confirm-service'),
                    description: t('confirm-service-warning'),
                    onConfirm: () => {
                        confirmMutate(row.service_id!)
                    },
                })
            },
            disabled: (row) =>
                row.service_status !== ServiceStatus.WaitingConfirm,
            hidden: isTotalRow,
        },
        {
            key: 'reject',
            label: t('service-rejected'),
            icon: <CancelIcons />,
            variant: 'danger',
            onClick: (row) => {
                if (!row.service_id) return
                setConfirmation({
                    open: true,
                    useReason: true,
                    title: t('rejected-service'),
                    description: t('rejected-service-warning'),
                    onConfirm: (reason) => {
                        rejectMutate({
                            serviceID: row.service_id!,
                            reason: reason as string,
                        })
                    },
                })
            },
            disabled: (row) =>
                row.service_status !== ServiceStatus.WaitingConfirm,
            hidden: isTotalRow,
        },
        {
            key: 'cancel',
            label: t('cancel-service'),
            icon: <CancelIcons />,
            variant: 'danger',
            onClick: (row) => {
                setModal('ServiceModals/CancelledServiceModal/index', {
                    service: row,
                })
            },
            disabled: (row) => row.service_status !== ServiceStatus.Active,
            hidden: isTotalRow,
        },
    ]
}
